import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, X, Mail, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "../contexts/CartContext";

const money = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CartIcon = ({ darkBg = false }) => {
  const { items, count, removeItem, dueToday, monthly } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const colorClass = darkBg ? "text-white hover:text-knight-gold" : "text-knight-navy hover:text-knight-gold";

  const emailCart = () => {
    const lines = items.map(i => `• ${i.name} — ${i.setupFee ? `$${i.setupFee} first work fee` : ""}${i.monthly ? ` $${i.monthly}/mo` : ""}${i.oneTime ? ` $${i.oneTime} one-time` : ""}`).join("%0A");
    const url = `${window.location.origin}/checkout`;
    const body = `Hi,%0A%0AHere's the cart I'd like to save:%0A%0A${lines}%0A%0ATotal due today: ${money(dueToday)}%0AMonthly: ${money(monthly)}/mo%0A%0AResume checkout: ${url}`;
    const mailto = `mailto:?subject=My%20Knight%20Credit%20Cart&body=${body}`;
    window.location.href = mailto;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Cart"
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${colorClass}`}
      >
        <ShoppingCart size={20} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-knight-gold text-knight-navy text-[10px] font-extrabold flex items-center justify-center ring-2 ring-white">
            {count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 knight-gradient text-white">
              <div className="flex items-center gap-2">
                <ShoppingCart size={16} className="text-knight-gold" />
                <p className="font-display font-bold text-base">Your Cart</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/70 hover:text-white"><X size={16} /></button>
            </div>

            <div className="max-h-72 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-sm text-knight-navy/60">
                  <ShoppingCart size={28} className="mx-auto mb-2 text-slate-300" />
                  Your cart is empty.
                  <Link to="/pricing" onClick={() => setOpen(false)} className="block mt-3 font-semibold text-knight-navy underline">Browse plans</Link>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map(it => (
                    <li key={it.id} className="px-4 py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-knight-navy truncate">{it.name}</p>
                        <p className="text-[11px] text-knight-navy/55 mt-0.5">
                          {it.setupFee ? `${money(it.setupFee)} first fee` : ""}
                          {it.monthly ? ` · ${money(it.monthly)}/mo` : ""}
                          {it.oneTime ? `${money(it.oneTime)} one-time` : ""}
                        </p>
                      </div>
                      <button onClick={() => removeItem(it.id)} aria-label="Remove" className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-slate-200 p-4 bg-slate-50/60">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-xs font-semibold text-knight-navy/70">Due Today</span>
                  <span className="font-display font-bold text-xl text-knight-navy">{money(dueToday)}</span>
                </div>
                <Button onClick={() => { setOpen(false); navigate("/checkout"); }} className="w-full bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold">
                  <Lock size={14} className="mr-2" /> Secure Checkout
                </Button>
                <button onClick={emailCart} className="w-full mt-2 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-knight-navy/65 hover:text-knight-navy py-1">
                  <Mail size={12} /> Email me this cart
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CartIcon;
